'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { signOut, useSession } from 'next-auth/react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

const INACTIVITY_LIMIT_MS = 5 * 60 * 1000;
const WARNING_COUNTDOWN_SECONDS = 60;
const WARNING_COUNTDOWN_MS = WARNING_COUNTDOWN_SECONDS * 1000;
const LAST_ACTIVITY_KEY = 'workforce:lastActivityAt';
const IDLE_WARNING_KEY = 'workforce:idleWarningAt';
const IDLE_LOGOUT_KEY = 'workforce:idleLogoutAt';
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'pointerdown',
] as const;

export function InactivityLogout() {
  const { status } = useSession();
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(WARNING_COUNTDOWN_SECONDS);
  const timeoutRef = useRef<number | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);
  const warningActiveRef = useRef(false);
  const hasSignedOutRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      window.clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const idleSignOut = useCallback(() => {
    if (hasSignedOutRef.current) return;
    hasSignedOutRef.current = true;
    clearTimers();
    try {
      window.localStorage.removeItem(LAST_ACTIVITY_KEY);
      window.localStorage.removeItem(IDLE_WARNING_KEY);
      window.localStorage.setItem(IDLE_LOGOUT_KEY, String(Date.now()));
    } catch {
      // localStorage can be unavailable in private browsing or locked-down contexts.
    }
    void signOut({ callbackUrl: '/auth/signin' });
  }, [clearTimers]);

  const startWarningCountdown = useCallback(
    (warningStartedAt: number, shouldBroadcast: boolean) => {
      if (hasSignedOutRef.current) return;
      clearTimers();
      warningActiveRef.current = true;
      setIsWarningOpen(true);

      if (shouldBroadcast) {
        try {
          window.localStorage.setItem(IDLE_WARNING_KEY, String(warningStartedAt));
        } catch {
          // The current tab still shows the warning if cross-tab storage is blocked.
        }
      }

      const updateCountdown = () => {
        const elapsedMs = Date.now() - warningStartedAt;
        const remainingMs = WARNING_COUNTDOWN_MS - elapsedMs;
        if (remainingMs <= 0) {
          setCountdownSeconds(0);
          idleSignOut();
          return;
        }
        setCountdownSeconds(Math.ceil(remainingMs / 1000));
      };

      updateCountdown();
      countdownIntervalRef.current = window.setInterval(updateCountdown, 1000);
      timeoutRef.current = window.setTimeout(idleSignOut, Math.max(0, WARNING_COUNTDOWN_MS - (Date.now() - warningStartedAt)));
    },
    [clearTimers, idleSignOut]
  );

  const scheduleWarning = useCallback(
    (lastActivityAt: number) => {
      clearTimers();
      warningActiveRef.current = false;
      setIsWarningOpen(false);
      setCountdownSeconds(WARNING_COUNTDOWN_SECONDS);
      const elapsedMs = Date.now() - lastActivityAt;
      const remainingMs = INACTIVITY_LIMIT_MS - elapsedMs;
      if (remainingMs <= 0) {
        startWarningCountdown(Date.now(), true);
        return;
      }
      timeoutRef.current = window.setTimeout(() => startWarningCountdown(Date.now(), true), remainingMs);
    },
    [clearTimers, startWarningCountdown]
  );

  const recordActivity = useCallback(() => {
    if (hasSignedOutRef.current) return;
    if (warningActiveRef.current) return;
    const now = Date.now();
    try {
      window.localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    } catch {
      // Keep the current tab protected even if cross-tab storage is blocked.
    }
    scheduleWarning(now);
  }, [scheduleWarning]);

  const confirmStillHere = useCallback(() => {
    const now = Date.now();
    warningActiveRef.current = false;
    setIsWarningOpen(false);
    setCountdownSeconds(WARNING_COUNTDOWN_SECONDS);
    try {
      window.localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
      window.localStorage.removeItem(IDLE_WARNING_KEY);
    } catch {
      // Keep this tab active even if cross-tab storage is blocked.
    }
    scheduleWarning(now);
  }, [scheduleWarning]);

  useEffect(() => {
    if (status !== 'authenticated') {
      hasSignedOutRef.current = false;
      warningActiveRef.current = false;
      setIsWarningOpen(false);
      clearTimers();
      return;
    }

    let lastActivityAt = Date.now();
    try {
      const stored = Number(window.localStorage.getItem(LAST_ACTIVITY_KEY));
      if (Number.isFinite(stored) && stored > 0) {
        lastActivityAt = stored;
      } else {
        window.localStorage.setItem(LAST_ACTIVITY_KEY, String(lastActivityAt));
      }
    } catch {
      // Fall back to this tab's in-memory timer.
    }

    scheduleWarning(lastActivityAt);

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, recordActivity, { passive: true });
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === LAST_ACTIVITY_KEY && event.newValue) {
        const nextActivityAt = Number(event.newValue);
        if (Number.isFinite(nextActivityAt) && nextActivityAt > 0) {
          scheduleWarning(nextActivityAt);
        }
      }
      if (event.key === IDLE_WARNING_KEY && event.newValue) {
        const warningStartedAt = Number(event.newValue);
        if (Number.isFinite(warningStartedAt) && warningStartedAt > 0) {
          startWarningCountdown(warningStartedAt, false);
        }
      }
      if (event.key === IDLE_LOGOUT_KEY && event.newValue) {
        idleSignOut();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, recordActivity);
      }
      window.removeEventListener('storage', handleStorage);
      clearTimers();
    };
  }, [clearTimers, idleSignOut, recordActivity, scheduleWarning, startWarningCountdown, status]);

  return (
    <Dialog open={isWarningOpen} aria-labelledby="inactivity-logout-title">
      <DialogTitle id="inactivity-logout-title">Are you still there?</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography>
            You have been inactive for 5 minutes. You will be logged out due to inactivity unless you confirm you are still here.
          </Typography>
          <Typography variant="h6" color="error">
            Logging out in {countdownSeconds} second{countdownSeconds === 1 ? '' : 's'}.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={idleSignOut} color="inherit">
          Log out now
        </Button>
        <Button onClick={confirmStillHere} variant="contained" autoFocus>
          I&apos;m still here
        </Button>
      </DialogActions>
    </Dialog>
  );
}
